import React, { useState, useEffect, useContext, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { InternalLayout } from "../Components/InternalLayout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Announcement as AnnouncementIcon,
  Image as ImageIcon,
  SelectAll as SelectAllIcon,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";
import { announcementExporter } from "../utils/announcementExport";

function Announcements() {
  const { user, token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [announcements, setAnnouncements] = useState([]);
  const [expiredAnnouncements, setExpiredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "info",
  });
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    endingDate: "",
  });
  
  // Estados para seleção e exportação
  const [selectedAnnouncements, setSelectedAnnouncements] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);

  // Função para mostrar alertas
  const showAlert = (message, type = "info") => {
    setAlert({ open: true, message, type });
    setTimeout(
      () => setAlert({ open: false, message: "", type: "info" }),
      5000,
    );
  };

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("/api/secretaria/avisos"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filtrar avisos não expirados
        const now = new Date();
        const activeAnnouncements = data.announcements.filter(
          (announcement) => {
            if (!announcement.endingDate) return true; // Sem data de fim = sempre ativo
            return new Date(announcement.endingDate) >= now;
          },
        );
        setAnnouncements(activeAnnouncements);

        const expiredAnnouncements = data.announcements.filter(
          (announcement) => {
            if (!announcement.endingDate) return false; // Sem data de fim = sempre ativo
            return new Date(announcement.endingDate) < now;
          },
        );
        setExpiredAnnouncements(expiredAnnouncements);
      } else {
        showAlert("Erro ao carregar avisos", "error");
      }
    } catch (error) {
      console.error("Erro ao buscar avisos:", error);
      showAlert("Erro ao carregar avisos", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user && token) {
      fetchAnnouncements();
    }
  }, [user, token, fetchAnnouncements]);

  // Verificar se é admin
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Acesso negado. Esta página é apenas para usuários autenticados.
        </Alert>
      </Box>
    );
  }

  const handleOpenDialog = (announcementToEdit = null) => {
    if (announcementToEdit) {
      setEditingAnnouncement(announcementToEdit);
      setFormData({
        title: announcementToEdit.title,
        content: announcementToEdit.content,
        endingDate: announcementToEdit.endingDate,
      });
    } else {
      setEditingAnnouncement(null);
      setFormData({
        title: "",
        content: "",
        endingDate: "",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAnnouncement(null);
    setFormData({
      title: "",
      content: "",
      endingDate: "",
    });
  };
  const handleOpenDeleteDialog = (announcement) => {
    setAnnouncementToDelete(announcement);
    setDeleteDialogOpen(true);
  };

  const handleInputChange = (field) => (event) => {
    if (field === "active") {
      setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    }
  };

  const handleSubmit = async () => {
    try {
      const method = editingAnnouncement ? "PUT" : "POST";
      const body = editingAnnouncement
        ? { announcementId: editingAnnouncement._id, ...formData }
        : formData;

      const response = await fetch(getApiUrl("/api/secretaria/avisos"), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        handleCloseDialog();
        fetchAnnouncements();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao salvar aviso", "error");
      }
    } catch (error) {
      console.error("Erro ao salvar aviso:", error);
      showAlert("Erro ao salvar aviso", "error");
    }
  };

  const handleDeleteAnnouncement = async () => {
    try {
      const response = await fetch(getApiUrl("/api/secretaria/avisos"), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ announcementId: announcementToDelete._id }),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        setDeleteDialogOpen(false);
        setAnnouncementToDelete(null);
        fetchAnnouncements();
      } else {
        const error = await response.json();
        showAlert(error.message || "Erro ao remover aviso", "error");
      }
    } catch (error) {
      console.error("Erro ao remover aviso:", error);
      showAlert("Erro ao remover aviso", "error");
    }
  };

  // Funções para seleção e exportação
  const handleToggleSelection = (announcementId) => {
    const newSelected = new Set(selectedAnnouncements);
    if (newSelected.has(announcementId)) {
      newSelected.delete(announcementId);
    } else {
      newSelected.add(announcementId);
    }
    setSelectedAnnouncements(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedAnnouncements.size === announcements.length) {
      setSelectedAnnouncements(new Set());
    } else {
      setSelectedAnnouncements(new Set(announcements.map(ann => ann._id)));
    }
  };

  const generateA5Image = async () => {
    setExportLoading(true);
    
    await announcementExporter.exportToA5Image(
      announcements,
      selectedAnnouncements,
      (message) => {
        showAlert(message, "success");
        setSelectedAnnouncements(new Set()); // Limpar seleção após exportar
        setExportLoading(false);
      },
      (error) => {
        showAlert(error, "error");
        setExportLoading(false);
      }
    );
  };



  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <InternalLayout>
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "100vh",
          pt: { xs: "56px", sm: "64px" },
          pb: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Alert */}
        {alert.open && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2 }}
            onClose={() => setAlert({ open: false, message: "", type: "info" })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 },
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: { xs: "1.5rem", md: "2.125rem" },
            }}
          >
            <AnnouncementIcon sx={{ fontSize: { xs: 24, md: 32 } }} /> Gerenciar
            Avisos
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Botões de seleção e exportação */}
            {announcements.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<SelectAllIcon />}
                  onClick={handleSelectAll}
                  sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                >
                  {selectedAnnouncements.size === announcements.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<ImageIcon />}
                  onClick={generateA5Image}
                  disabled={selectedAnnouncements.size === 0 || exportLoading}
                  color="success"
                  sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                >
                  {exportLoading ? 'Gerando...' : `Exportar Imagem${selectedAnnouncements.size > 0 ? ` (${selectedAnnouncements.size})` : ''}`}
                </Button>
              </>
            )}
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              color="primary"
              sx={{
                width: { xs: "100%", sm: "auto" },
                py: { xs: 1, md: 1.5 },
              }}
            >
              Novo Aviso
            </Button>
          </Box>
        </Box>

        {/* Estatísticas */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
              >
                Total de Avisos
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontSize: { xs: "1.75rem", md: "3rem" } }}
              >
                {announcements.length}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Layout Mobile - Cards */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {announcements.map((announcement) => {
            const isExpiringSoon =
              announcement.endingDate &&
              new Date(announcement.endingDate) <=
                new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

            return (
              <Card
                key={announcement._id}
                sx={{
                  mb: 2,
                  border: isExpiringSoon ? "2px solid #ff9800" : undefined,
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                      <Checkbox
                        checked={selectedAnnouncements.has(announcement._id)}
                        onChange={() => handleToggleSelection(announcement._id)}
                        size="small"
                        sx={{ mt: -0.5, mr: 1 }}
                        inputProps={{ 'aria-label': `Selecionar ${announcement.title}` }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", flex: 1 }}
                      >
                        {announcement.title}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        onClick={() => handleOpenDialog(announcement)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDeleteDialog(announcement)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {announcement.content}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {announcement.endingDate ? (
                      <Typography
                        variant="caption"
                        color={
                          isExpiringSoon ? "warning.main" : "text.secondary"
                        }
                        sx={{ fontWeight: isExpiringSoon ? "bold" : "normal" }}
                      >
                        {isExpiringSoon ? "⚠️ " : ""}Data de fim:{" "}
                        {new Date(announcement.endingDate).toLocaleDateString(
                          "pt-BR",
                        )}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ fontWeight: "bold" }}
                      >
                        ✅ Sem data de expiração
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          {announcements.length === 0 && (
            <Card>
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  Nenhum aviso ativo encontrado
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        <Box>
          <Typography
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: "0.875rem", md: "1rem" }, pt: 2 }}
          >
            Avisos Correntes
          </Typography>
        </Box>
        {/* Layout Desktop - Tabela */}
        <TableContainer
          component={Paper}
          sx={{ overflowX: "auto", display: { xs: "none", md: "block" } }}
        >
          <Table sx={{ minWidth: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", width: 50 }}>
                  <Checkbox
                    checked={announcements.length > 0 && selectedAnnouncements.size === announcements.length}
                    indeterminate={selectedAnnouncements.size > 0 && selectedAnnouncements.size < announcements.length}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'Selecionar todos' }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Título</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Conteúdo</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Data de Fim</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcements.length > 0 ? (
                announcements.map((announcement) => {
                  const isExpiringSoon =
                    announcement.endingDate &&
                    new Date(announcement.endingDate) <=
                      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

                  return (
                    <TableRow
                      key={announcement._id}
                      hover
                      sx={{
                        backgroundColor: isExpiringSoon ? "#fff3e0" : undefined,
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedAnnouncements.has(announcement._id)}
                          onChange={() => handleToggleSelection(announcement._id)}
                          inputProps={{ 'aria-label': `Selecionar ${announcement.title}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {isExpiringSoon ? "⚠️ " : ""}
                            {announcement.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 300, whiteSpace: "pre-wrap" }}>
                        <Typography variant="body2">
                          {announcement.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {announcement.endingDate ? (
                          <Typography
                            variant="body2"
                            color={
                              isExpiringSoon ? "warning.main" : "text.primary"
                            }
                            sx={{
                              fontWeight: isExpiringSoon ? "bold" : "normal",
                            }}
                          >
                            {new Date(
                              announcement.endingDate,
                            ).toLocaleDateString("pt-BR")}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="success.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            Indefinido
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            onClick={() => handleOpenDialog(announcement)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(announcement)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum aviso ativo encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box>
          <Typography
            color="text.secondary"
            gutterBottom
            sx={{ fontSize: { xs: "0.875rem", md: "1rem" }, pt: 5 }}
          >
            Avisos Expirados
          </Typography>
        </Box>
        {/* Layout Desktop - Tabela Avisos expirados */}
        <TableContainer
          component={Paper}
          sx={{ overflowX: "auto", display: { xs: "none", md: "block" } }}
        >
          <Table sx={{ minWidth: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Título</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Conteúdo</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Data de Fim</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expiredAnnouncements.length > 0 ? (
                expiredAnnouncements.map((announcement) => {
                  return (
                    <TableRow
                      key={announcement._id}
                      hover
                      sx={{
                        backgroundColor: "rgba(233, 29, 56, 0.1)",
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {announcement.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 300, whiteSpace: "pre-wrap" }}>
                        <Typography variant="body2">
                          {announcement.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {announcement.endingDate ? (
                          <Typography
                            variant="body2"
                            color={"warning.main"}
                            sx={{
                              fontWeight: "bold",
                            }}
                          >
                            {new Date(
                              announcement.endingDate,
                            ).toLocaleDateString("pt-BR")}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="success.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            Indefinido
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            onClick={() => handleOpenDialog(announcement)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(announcement)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum aviso ativo encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Dialog para criar/editar aviso */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              mx: { xs: 1, sm: 2 },
              my: { xs: 2, sm: 4 },
              maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 64px)" },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            {editingAnnouncement ? "Editar Aviso" : "Novo Aviso"}
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, sm: 2.5 },
                mt: 1,
              }}
            >
              <TextField
                label="Título"
                value={formData.title}
                onChange={handleInputChange("title")}
                fullWidth
                required
              />
              <TextField
                label="Conteúdo"
                value={formData.content}
                onChange={handleInputChange("content")}
                fullWidth
                required
                multiline
                rows={4}
                helperText="Descreva o conteúdo do aviso"
              />
              <TextField
                label="Data de Fim"
                type="date"
                value={
                  formData.endingDate
                    ? formData.endingDate.substring(0, 10)
                    : ""
                }
                onChange={handleInputChange("endingDate")}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Data até quando o aviso será exibido (opcional)"
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              gap: { xs: 1, sm: 1 },
            }}
          >
            <Button onClick={handleCloseDialog} fullWidth={isMobile}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth={isMobile}
              disabled={!formData.title || !formData.content}
            >
              {editingAnnouncement ? "Salvar" : "Criar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação para deletar */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              mx: { xs: 1, sm: 2 },
              my: { xs: 4, sm: 8 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>Confirmar Remoção</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography variant="body2">
              Tem certeza que deseja remover o aviso{" "}
              <strong>{announcementToDelete?.title}</strong>? Esta ação não pode
              ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 3 },
              pb: { xs: 2, sm: 3 },
              gap: { xs: 1, sm: 1 },
            }}
          >
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteAnnouncement}
              color="error"
              variant="contained"
              fullWidth={isMobile}
            >
              Remover
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </InternalLayout>
  );
}

export default Announcements;
